// Meta Business Agent, from the portal.
//
// Businesses turn this on themselves, so every call the backend exposes is
// reachable here rather than only in Meta's own tooling.
//
// THE ONE THING A CALLER OF THIS MODULE MUST NOT DO is treat a failed
// eligibility check as "not eligible". The backend answers three outcomes on
// purpose — eligible, not eligible, and NOT CHECKED — because Meta returns a
// bare boolean with no reason, and an HTTP error means the question could not be
// asked at all. Measured on the live estate: of five numbers, ONE was eligible,
// ZERO were ineligible, and THREE could not be asked because the Meta Business
// AI terms had not been accepted for that WhatsApp Business Account. Rendering
// those three as "not eligible" tells a business Meta refused them when nobody
// ever asked.
import api from '../axios.config';
import { responseFrom, errorFrom } from '../apiEnvelope';
import type { APIResponse } from 'types/ivr';

const base = (phoneNumberId: string) =>
  `/v1/whatsapp/agent/${encodeURIComponent(phoneNumberId)}`;

/** The three outcomes of asking Meta whether a number may run the agent. */
export interface AgentEligibility {
  phoneNumberId: string;
  /** false means the question could not be asked — NOT that the answer was no. */
  checked: boolean;
  /** null whenever `checked` is false. */
  eligible: boolean | null;
  status?: number;
  /** Meta's own words, when it refused to answer. */
  reason?: string;
}

/**
 * Whether Meta's refusal is the terms-of-service one.
 *
 * <p>Worth singling out because it is by far the most common reason on this
 * estate and it is the only one with a precise, actionable fix — somebody with
 * portfolio admin accepts the terms in WhatsApp Manager. Everything else that
 * comes back here is a support conversation; this one is a click.
 */
export const isTermsNotAccepted = (e?: AgentEligibility | null): boolean =>
  !!e && e.checked === false && /Meta Business AI Terms/i.test(e.reason || '');

export const whatsappAgentService = {
  eligibility: async (phoneNumberId: string): Promise<APIResponse<AgentEligibility>> => {
    try {
      return responseFrom<AgentEligibility>((await api.get(`${base(phoneNumberId)}/eligibility`)).data);
    } catch (e: any) { return errorFrom(e, 'Could not check whether this number can run an agent'); }
  },

  /**
   * Onboard AND switch on, in one call.
   *
   * The backend deliberately refuses to do only half: Meta's onboarding call
   * creates the configuration while the agent still answers nobody until
   * settings sets rollout.enabled, and both succeed independently. Two buttons
   * here would recreate exactly the half-on state the backend exists to prevent.
   */
  enable: async (phoneNumberId: string, escalationPartnerId?: string): Promise<APIResponse<any>> => {
    try {
      const q = escalationPartnerId
        ? `?escalationPartnerId=${encodeURIComponent(escalationPartnerId)}` : '';
      return responseFrom((await api.post(`${base(phoneNumberId)}/enable${q}`)).data);
    } catch (e: any) { return errorFrom(e, 'Could not switch the agent on'); }
  },

  disable: async (phoneNumberId: string): Promise<APIResponse<any>> => {
    try {
      return responseFrom((await api.post(`${base(phoneNumberId)}/disable`)).data);
    } catch (e: any) { return errorFrom(e, 'Could not switch the agent off'); }
  },

  settings: async (phoneNumberId: string): Promise<APIResponse<any>> => {
    try {
      return responseFrom((await api.get(`${base(phoneNumberId)}/settings`)).data);
    } catch (e: any) { return errorFrom(e, 'Could not read this agent’s settings'); }
  },

  /** Tone, priorities, brand voice. */
  skills: async (phoneNumberId: string, systemInstructions: string): Promise<APIResponse<any>> => {
    try {
      return responseFrom((await api.post(`${base(phoneNumberId)}/skills`, { systemInstructions })).data);
    } catch (e: any) { return errorFrom(e, 'Could not save those instructions'); }
  },

  businessInfo: async (phoneNumberId: string, body: {
    businessName?: string; hours?: string; locations?: string[];
  }): Promise<APIResponse<any>> => {
    try {
      return responseFrom((await api.post(`${base(phoneNumberId)}/knowledge/business-info`, body)).data);
    } catch (e: any) { return errorFrom(e, 'Could not save the business details'); }
  },

  /** One question and its answer — Meta models FAQs one at a time. */
  faq: async (phoneNumberId: string, question: string, answer: string): Promise<APIResponse<any>> => {
    try {
      return responseFrom((await api.post(`${base(phoneNumberId)}/knowledge/faq`, { question, answer })).data);
    } catch (e: any) { return errorFrom(e, 'Could not save that question'); }
  },

  website: async (phoneNumberId: string, url: string): Promise<APIResponse<any>> => {
    try {
      return responseFrom((await api.post(`${base(phoneNumberId)}/knowledge/website`, { url })).data);
    } catch (e: any) { return errorFrom(e, 'Could not add that website'); }
  },
};
